const TEXT_DIM = 16;
const DEFAULT_CHARS_PER_SECOND = 4.5;

export class TextFeatureTimeline {
  private readonly segments: TextSegment[];
  private readonly totalSeconds: number;

  constructor(text: string, private readonly fps = 30) {
    this.segments = segmentText(text);
    const charCount = Math.max(1, this.segments.reduce((sum, item) => sum + Math.max(1, item.text.length), 0));
    this.totalSeconds = Math.max(1, charCount / DEFAULT_CHARS_PER_SECOND);
  }

  featureAtFrame(frame: number): Float32Array<ArrayBuffer> {
    const feature = new Float32Array(TEXT_DIM);
    if (this.segments.length === 0) {
      feature[1] = 1;
      return feature;
    }
    const time = Math.max(0, frame / this.fps);
    const globalProgress = Math.min(1, time / this.totalSeconds);
    const segmentIndex = Math.min(this.segments.length - 1, Math.floor(globalProgress * this.segments.length));
    const segment = this.segments[segmentIndex];
    const localStart = segmentIndex / this.segments.length;
    const localEnd = (segmentIndex + 1) / this.segments.length;
    const localProgress = clamp01((globalProgress - localStart) / Math.max(1e-6, localEnd - localStart));

    feature[0] = segment.text.trim().length > 0 ? 1 : 0;
    feature[1] = segment.text.trim().length > 0 ? 0 : 1;
    feature[2] = globalProgress;
    feature[3] = localProgress;
    feature[4] = Math.min(1, segment.text.length / 12);
    writePunctuationFeatures(feature, segment.punctuation);
    feature[9] = Math.min(1, segment.text.trim().length / 8);
    const bucket = stableHash(segment.text) % Math.max(1, TEXT_DIM - 10);
    feature[10 + bucket] = segment.text.trim().length > 0 ? 1 : 0;
    return feature;
  }
}

interface TextSegment {
  text: string;
  punctuation: string;
}

function segmentText(text: string): TextSegment[] {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return [];
  }
  const matches = normalized.match(/[^，,。.!！？?；;：:\s]+[，,。.!！？?；;：:]?/g);
  return (matches ?? [normalized]).map((item) => {
    const punctuation = item.match(/[，,。.!！？?；;：:]$/)?.[0] ?? "";
    return { text: item.replace(/[，,。.!！？?；;：:]$/, ""), punctuation };
  });
}

function writePunctuationFeatures(target: Float32Array, punctuation: string): void {
  if ([",", "，", "、"].includes(punctuation)) target[5] = 1;
  if ([".", "。", ";", "；", ":"].includes(punctuation)) target[6] = 1;
  if (["?", "？"].includes(punctuation)) target[7] = 1;
  if (["!", "！"].includes(punctuation)) target[8] = 1;
}

function stableHash(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0);
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}
